import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Search, Shield, Wrench, Bell, LifeBuoy, Clock, Check, ArrowRight, Lock, Zap, Eye, RefreshCw, Database, Target, ShieldAlert, Activity, Flame, Globe } from "lucide-react";
import { Helmet } from "@/components/Helmet";
import { PUBLIC_OFFERS } from "../../shared/publicOffers";

const Services = () => {
  const services = [
    {
      icon: Zap,
      code: "MITIGATE",
      title: "Threat Detection and Containment",
      description:
        "Detects suspicious behaviour and applies safe, reversible containment when thresholds are exceeded.",
      features: [
        "Detects repeated login abuse and access spikes",
        "Contains activity with reversible lockouts and throttling",
        "Validates high-impact signals with human review",
        "Summaries prioritised by impact and exposure",
        "Application-level containment - no edge protection required",
      ],
    },
    {
      icon: Eye,
      code: "INTELLIGENCE",
      title: "Vulnerability Intelligence",
      description:
        "Tracks disclosures, exploitability, and exposure across your stack.",
      features: [
        "Monitors new core, plugin, and theme disclosures",
        "Prioritises risk by exploitability and usage",
        "Maps findings to your installed software",
        "Early visibility before public disclosure where possible",
        "Advisories translated into ShadowScan remediation steps",
      ],
    },
    {
      icon: Shield,
      code: "PROTECTION",
      title: "Application-Level Protections",
      description:
        "Reduces common WordPress attack paths while keeping sites stable.",
      features: [
        "Reduces exposed entry points like XML-RPC and enumeration",
        "Safe, reversible hardening toggles",
        "Login abuse containment with last-admin safety",
        "Configuration hygiene checks and recommendations",
        "Works alongside customer-owned edge protection",
      ],
    },
    {
      icon: Activity,
      code: "MONITORING",
      title: "Continuous Monitoring",
      description:
        "Signals, validation, and escalation with human oversight where included.",
      features: [
        "Monitors integrity changes and suspicious file activity",
        "Validates alerts to reduce noise",
        "Centralised reporting across monitored sites",
        "Incident timeline and evidence summaries",
        "Continuous monitoring with human validation",
      ],
    },
    {
      icon: RefreshCw,
      code: "UPDATES",
      title: "Recovery and Hardening",
      description:
        "Guided remediation and safe updates that reduce future exposure.",
      features: [
        "Guides safe core, plugin, and theme updates",
        "Prioritises remediation of high-risk items",
        "Post-incident hardening checkpoints",
        "Recovery coordination and follow-up verification",
        "Operational notes and runbooks",
      ],
    },
    {
      icon: LifeBuoy,
      code: "SOC_RESPONSE",
      title: "SOC Support (Premium)",
      description:
        "Analyst-led monitoring with incident ownership and remediation.",
      features: [
        "Active analyst monitoring and validation",
        "Containment and stabilization actions coordinated",
        "Remediation performed by ShadowScan for Premium",
        "Post-incident review and tuning",
        "Dedicated account management for portfolios",
      ],
    },
  ];

  const faqItems = [
    {
      question: "Do you include a firewall or WAF?",
      answer:
        "ShadowScan does not embed a firewall into WordPress. We focus on application-level protections, continuous monitoring, and human-reviewed guidance. If you already use a WAF or edge protection, ShadowScan works alongside it.",
    },
    {
      question: "What is included in ShadowScan's managed WordPress security service?",
      answer:
        "ShadowScan offers free ShadowScan Guard, Basic Hosting for hosted WordPress onboarding, monitored Essential coverage, and Premium analyst-led response support. Managed security coverage includes application-level protections, threat detection and containment, integrity and admin monitoring, and guidance aligned to Essential or Premium.",
    },
    {
      question: "Do you offer continuous monitored WordPress security?",
      answer:
        "Essential and Premium include continuous monitored security. Premium adds analyst-led response support within agreed scope. ShadowScan Guard and Basic Hosting do not include ShadowScan team monitoring.",
    },
    {
      question: "What happens if my WordPress site is compromised?",
      answer:
        "Response depends on plan. ShadowScan Guard remains self-serve, Basic Hosting covers hosted onboarding only, Essential adds validated guidance for your team, and Premium includes higher-touch analyst-led response coordination within agreed scope.",
    },
    {
      question: "How does ShadowScan reduce risk without a WAF?",
      answer:
        "ShadowScan focuses on application-level protections inside WordPress, continuous monitoring, and safe containment of suspicious behaviour. If you already use customer-owned edge protection, ShadowScan works alongside it."
    },
    {
      question: "How often do you update WordPress plugins and themes?",
      answer:
        "Update support depends on plan. ShadowScan Guard remains limited to essential local hardening, Basic Hosting covers hosted onboarding, Essential includes guidance and safe-update support where compatible, and Premium may include deeper hands-on response and remediation within agreed scope.",
    },
  ];

  const featureGroups = [
    {
      title: "Monitoring",
      items: [
        "Continuous monitoring with validated alerts",
        "Vulnerability intelligence with risk prioritisation",
        "Connector health and site inventory tracking",
        "Centralised reporting across monitored sites",
      ],
    },
    {
      title: "Protection Controls",
      items: [
        "Application-level protections",
        "Human oversight where the selected plan includes it",
        "Attack surface reduction guidance",
        "Login abuse containment and access control improvements",
        "Safe update workflows (core, plugins, themes)",
      ],
    },
    {
      title: "Incident Response",
      items: [
        "Human-led triage and response coordination",
        "Response playbooks aligned to severity",
        "Priority incident response for Premium",
        "Clear remediation guidance",
      ],
    },
    {
      title: "Recovery & Hardening",
      items: [
        "Malware cleanup coordination",
        "Root-cause analysis and recovery planning",
        "Post-incident review and hardening",
        "Ongoing security optimisation",
      ],
    },
  ];

  const serviceLevels = [
    {
      title: "ShadowScan Guard",
      badge: "Free",
      price: "Free",
      description:
        "Free WordPress protection layer for essential local hardening and basic status visibility.",
      points: [
        "Essential local hardening where supported",
        "Basic in-plugin visibility and self-check status",
        "No ShadowScan team monitoring or analyst response",
      ],
      ctaLabel: "Run Free Scan",
      ctaTo: "https://shadowscanguard.com.au",
    },
    {
      title: "Basic Hosting",
      badge: "Hosted",
      price: "AUD 12.99 / month",
      description:
        "Managed WordPress hosting with ShadowScan Guard onboarding, available on its own or paired with Essential or Premium.",
      points: [
        "Hosted WordPress onboarding and provisioning path",
        "ShadowScan Guard pairing during setup",
        "Can stay hosting-only or add Essential or Premium security",
        "No ShadowScan team monitoring or analyst-led response by itself",
      ],
      ctaLabel: "Start Basic Hosting",
      ctaTo: "/portal/add-domain?base=hosting_basic",
    },
    {
      title: "Essential",
      badge: "Monitored",
      price: "AUD 50 / site / month",
      description:
        "Monitored security with validated guidance and stronger managed oversight.",
      points: [
        "Monitored security with validated guidance",
        "Application-level protections and prioritized monitoring",
        "No Premium analyst-led incident ownership",
      ],
      ctaLabel: "Choose Essential",
      ctaTo: "/portal/add-domain?plan=essential",
    },
    {
      title: "Premium",
      badge: "Analyst-Led",
      price: "AUD 185 / site / month",
      description:
        "Higher-touch monitoring, response coordination, and analyst-led support within agreed scope.",
      points: [
        "Analyst-led monitoring and response coordination",
        "Incident ownership and recovery support within scope",
        "Highest-touch ShadowScan service level",
      ],
      ctaLabel: "Choose Premium",
      ctaTo: "/portal/add-domain?plan=premium",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>ShadowScan Services | Guard, Hosting, and Monitored Security</title>
        <meta
          name="description"
          content="WordPress security from free ShadowScan Guard through Basic Hosting, Essential monitored security, and Premium analyst-led response support."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "What is included in ShadowScan's managed WordPress security service?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "ShadowScan offers free ShadowScan Guard, Basic Hosting for hosted WordPress onboarding, monitored Essential coverage, and Premium analyst-led response support. Managed security coverage includes application-level protections, threat detection and containment, integrity and admin monitoring, and guidance aligned to Essential or Premium."
                }
              },
              {
                "@type": "Question",
                name: "How does ShadowScan reduce risk on WordPress sites?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We combine application-level protections, safe containment, and monitoring aligned to the selected service level. Human validation and analyst support apply only where the selected plan includes them."
                }
              },
              {
                "@type": "Question",
                name: "Do you include a firewall or WAF?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "ShadowScan does not embed a firewall into WordPress. We focus on application-level protections, continuous monitoring, and human-reviewed guidance. If you already use a WAF or edge protection, ShadowScan works alongside it."
                }
              },
              {
                "@type": "Question",
                name: "Do you offer continuous monitored WordPress security?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Essential and Premium include continuous monitored security. Premium adds analyst-led response support within agreed scope. ShadowScan Guard and Basic Hosting do not include ShadowScan team monitoring."
                }
              },
              {
                "@type": "Question",
                name: "What happens if my WordPress site is compromised?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Response depends on plan. ShadowScan Guard remains self-serve, Basic Hosting covers hosted onboarding only, Essential adds validated guidance for your team, and Premium includes higher-touch analyst-led response coordination within agreed scope."
                }
              },
              {
                "@type": "Question",
                name: "How often do you update WordPress plugins and themes?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Update support depends on plan. ShadowScan Guard remains limited to essential local hardening, Basic Hosting covers hosted onboarding, Essential includes guidance and safe-update support where compatible, and Premium may include deeper hands-on response and remediation within agreed scope."
                }
              },
              {
                "@type": "Question",
                name: "What is the difference between Basic Hosting, Essential, and Premium?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Basic Hosting (AUD $12.99/month) is a hosted WordPress onboarding offer that can be purchased alone or paired with Essential or Premium as a security add-on. Essential (AUD $50/site/month) adds monitored security with validated response guidance. Premium (AUD $185/site/month) adds analyst-led monitoring and priority response support within agreed scope."
                }
              }
            ]
          })}
        </script>
      </Helmet>
      <Navigation />
      <main className="flex-grow pt-24">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-20 -z-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent -z-10" />
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 mb-6">
                <span className="font-mono text-sm text-primary">// MANAGED_SECURITY</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6">
                ShadowScan <span className="text-gradient">Service Levels</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                ShadowScan spans free ShadowScan Guard, Basic Hosting, Essential monitored security, and Premium analyst-led response support so teams can choose the service level that fits their site.
              </p>
              <p className="text-sm text-muted-foreground/80 max-w-2xl mx-auto">
                Free ShadowScan Guard covers essential local hardening and basic visibility only. Basic Hosting adds hosted onboarding, while monitored security and analyst response require Essential or Premium.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 border-y border-border/50 bg-muted/20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-10">
              <div className="font-mono text-sm text-primary mb-4">// SERVICE_LEVELS</div>
              <h2 className="text-3xl font-heading font-bold mb-4">What’s Free and What Requires a Plan</h2>
              <p className="text-muted-foreground">
                ShadowScan Guard is the free protection layer. Basic Hosting can be purchased on its own or paired with Essential or Premium, and the managed security tiers add progressively higher-touch service levels.
              </p>
            </div>
            <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {serviceLevels.map((level) => (
                <Card key={level.title} className="cyber-card border-border/60">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-3">
                      <CardTitle className="text-lg font-heading">{level.title}</CardTitle>
                      <span className="font-mono text-[11px] uppercase tracking-wide text-primary border border-primary/30 rounded-full px-3 py-1">
                        {level.badge}
                      </span>
                    </div>
                    <CardDescription className="text-sm">{level.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm font-mono text-primary mb-4">{level.price}</p>
                    <ul className="space-y-2">
                      {level.points.map((point) => (
                        <li key={point} className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span className="text-xs text-muted-foreground">{point}</span>
                        </li>
                      ))}
                    </ul>
                    <Button asChild className="w-full mt-6" variant={level.title === 'Premium' ? 'default' : 'outline'}>
                      {level.title === 'ShadowScan Guard' ? (
                        <a href={level.ctaTo} target="_blank" rel="noopener noreferrer">
                          {level.ctaLabel}
                        </a>
                      ) : (
                        <Link to={level.ctaTo}>{level.ctaLabel}</Link>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mt-6">
              <Card className="cyber-card border-border/60">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Basic Hosting + Essential</CardTitle>
                  <CardDescription>Hosted WordPress onboarding with monitored Essential coverage.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm font-mono text-primary">AUD 62.99 / month</p>
                  <Button asChild className="w-full" variant="outline">
                    <Link to="/portal/add-domain?base=hosting_basic&addon=essential">Start Hosting + Essential</Link>
                  </Button>
                </CardContent>
              </Card>
              <Card className="cyber-card border-primary/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Basic Hosting + Premium</CardTitle>
                  <CardDescription>Hosted WordPress onboarding with Premium analyst-led support.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm font-mono text-primary">AUD 197.99 / month</p>
                  <Button asChild className="w-full">
                    <Link to="/portal/add-domain?base=hosting_basic&addon=premium">Start Hosting + Premium</Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>


        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <Card key={index} className="cyber-card border-border hover:border-primary/50 transition-all duration-300 group">
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-primary/10 border border-primary/30 rounded-lg flex items-center justify-center">
                          <Icon className="h-6 w-6 text-primary" />
                        </div>
                        <span className="font-mono text-xs text-primary/60 bg-primary/5 px-2 py-1 rounded">{service.code}</span>
                      </div>
                      <CardTitle className="text-lg font-heading">{service.title}</CardTitle>
                      <CardDescription className="text-sm">{service.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {service.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <Check className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                            <span className="text-xs text-muted-foreground">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <div className="font-mono text-sm text-primary mb-4">// FEATURE_GROUPS</div>
              <h2 className="text-3xl font-heading font-bold mb-4">What You Get</h2>
              <p className="text-muted-foreground">
                Structured coverage across protection, monitoring, guidance, and response support, with availability varying by service level.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {featureGroups.map((group) => (
                <Card key={group.title} className="cyber-card border-border/60">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">{group.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      {group.items.map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <div className="font-mono text-sm text-primary mb-4">// INCIDENT_FLOW</div>
              <h2 className="text-3xl font-heading font-bold mb-4">What Happens During an Incident</h2>
              <p className="text-muted-foreground">
                Detect - contain - validate - respond, with the level of ShadowScan involvement depending on the service tier.
              </p>
            </div>
            <div className="grid md:grid-cols-4 gap-4 max-w-5xl mx-auto mb-8">
              {[
                { title: "Detect", detail: "Suspicious behaviour and integrity changes are flagged." },
                { title: "Contain", detail: "Safe, reversible controls limit impact." },
                { title: "Validate", detail: "Analysts verify high-risk signals before escalation." },
                { title: "Respond", detail: "Clear actions and remediation are coordinated." },
              ].map((step) => (
                <Card key={step.title} className="cyber-card border-border/60">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">{step.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{step.detail}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <Card className="cyber-card border-border/60">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">ShadowScan Guard / Basic Hosting</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    ShadowScan Guard remains self-serve, and Basic Hosting covers hosted onboarding. Neither includes ShadowScan monitored security or analyst-led response by itself.
                  </p>
                </CardContent>
              </Card>
              <Card className="cyber-card border-border/60">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Essential Outcome</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    ShadowScan contains suspicious activity automatically, validates high-risk alerts, and provides clear remediation steps for your team to execute.
                  </p>
                </CardContent>
              </Card>
              <Card className="cyber-card border-primary/40">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Premium Outcome</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    ShadowScan takes incident ownership - we validate, contain, remediate, and coordinate recovery to stabilize the site quickly.
                  </p>
                </CardContent>
              </Card>
            </div>
            <p className="text-xs text-muted-foreground text-center mt-8">
              No security service can eliminate risk entirely. ShadowScan reduces risk, detects issues early, and responds quickly to limit impact.
            </p>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <div className="font-mono text-sm text-primary mb-4">// ADDITIONAL_OFFERS</div>
              <h2 className="text-3xl font-heading font-bold mb-4">ShadowScan Guard for WordPress</h2>
              <p className="text-muted-foreground">
                Separate ShadowScan Guard and Basic Hosting offers. Basic Hosting can stand alone or be paired with Essential or Premium when you want hosted onboarding plus managed security.
              </p>
              <p className="text-xs text-muted-foreground mt-3">
                ShadowScan is part of Valnex Group.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {PUBLIC_OFFERS.map((offer) => {
                const OfferIcon = offer.key === "hosting_basic" ? Globe : ShieldAlert;
                return (
                  <Card key={offer.key} className="cyber-card border-border/60 hover:border-primary/40 transition-all duration-300">
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between gap-4 mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-primary/10 border border-primary/30 rounded-lg flex items-center justify-center">
                            <OfferIcon className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <div className="font-mono text-xs text-primary">{offer.label}</div>
                            <CardTitle className="text-lg font-heading mt-1">{offer.title}</CardTitle>
                          </div>
                        </div>
                        <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground border border-border/60 rounded-full px-3 py-1">
                          {offer.status}
                        </span>
                      </div>
                      <CardDescription className="text-sm">{offer.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm font-mono text-primary mb-4">{offer.priceLabel}</p>
                      <ul className="space-y-2 mb-4">
                        {offer.features.map((feature) => (
                          <li key={feature} className="flex items-start gap-2">
                            <Check className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                            <span className="text-xs text-muted-foreground">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      {offer.lifecycle?.length ? (
                        <p className="text-xs text-muted-foreground mb-4">
                          Protection activation path: {offer.lifecycle.join(" -> ")}
                        </p>
                      ) : null}
                      <p className="text-xs text-muted-foreground">{offer.note}</p>
                      <Button asChild className="w-full mt-6" variant="outline">
                        {offer.key === "hosting_basic" ? (
                          <Link to="/portal/add-domain?base=hosting_basic">
                            Start Basic Hosting
                          </Link>
                        ) : (
                          <a href="https://shadowscanguard.com.au" target="_blank" rel="noopener noreferrer">
                            Run Free Scan
                          </a>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <div className="font-mono text-sm text-primary mb-4">// FAQ</div>
              <h2 className="text-3xl font-heading font-bold mb-4">Frequently Asked Questions</h2>
              <p className="text-muted-foreground">
                Straight answers on what ShadowScan provides and how we work.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {faqItems.map((item) => (
                <Card key={item.question} className="cyber-card border-border/60">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{item.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{item.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 relative">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center cyber-card rounded-2xl p-12 border-primary/30">
              <div className="font-mono text-sm text-primary mb-4">// GET_STARTED</div>
              <h2 className="text-3xl font-heading font-bold mb-4">Ready to Secure Your Site?</h2>
              <p className="text-muted-foreground mb-8">Start with free ShadowScan Guard or choose a managed plan that matches your service needs.</p>
              <Button size="lg" asChild className="bg-accent hover:bg-accent/90 text-accent-foreground">
                <Link to="/scan"><Lock className="mr-2 h-5 w-5" />Get Started Now<ArrowRight className="ml-2 h-5 w-5" /></Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Services;
